import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, X, Smartphone, Share, PlusSquare, Monitor } from 'lucide-react';
import { useRegisterOverlay } from '../contexts/OverlayQueueContext';

let globalDeferredPrompt = null;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Relying on native prompt priority
    globalDeferredPrompt = e;
    window.deferredPrompt = e;
  });
}

const InstallPrompt = () => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(globalDeferredPrompt);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showPWAReminder, setShowPWAReminder] = useState(false);
  const [showManualGuide, setShowManualGuide] = useState(false);

  const isEligible = showManualGuide ||
    (showInstallPrompt && !sessionStorage.getItem('pwa-reminder-dismissed')) ||
    (showPWAReminder && !sessionStorage.getItem('pwa-reminder-dismissed'));
  const { isActive } = useRegisterOverlay('installPrompt', 3, isEligible);

  // Check if device is mobile
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  };

  // Check if running as PWA
  const isPWA = () => {
    return window.matchMedia('(display-mode: standalone)').matches || 
           window.navigator.standalone === true;
  };

  // Check if PWA is installed (best effort detection)
  const checkPWAInstalled = () => {
    // Check if running in standalone mode
    if (isPWA()) {
      return true;
    }

    // Check localStorage for installation marker
    const installDate = localStorage.getItem('pwa-installed-date');
    if (installDate) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    // Only run on mobile devices
    if (!isMobile()) {
      return;
    }

    // Check if already running as PWA
    if (isPWA()) {
      // User is already in PWA mode, no need to show prompts
      return;
    }

    // Check if PWA was previously installed
    const pwaInstalled = checkPWAInstalled();
    
    if (pwaInstalled) {
      // PWA is installed but user is browsing via mobile browser
      // Show reminder to use installed app
      setShowPWAReminder(true);
    } else {
      // PWA not installed, check if recently dismissed (throttle for 24 hours)
      const dismissedDateStr = localStorage.getItem('pwa-prompt-dismissed-date');
      let isThrottled = false;
      if (dismissedDateStr) {
        const dismissedDate = new Date(dismissedDateStr);
        const oneDayLater = new Date(dismissedDate.getTime() + 24 * 60 * 60 * 1000);
        if (new Date() < oneDayLater) {
          isThrottled = true;
        }
      }
      if (!isThrottled) {
        setShowInstallPrompt(true);
      }
    }

    // Listen for beforeinstallprompt event
    const handler = (e) => {
      // Capture the deferred prompt so handleInstall() can call .prompt() natively.
      // Do NOT hide the install UI — keep the card visible so the user can tap
      // the "Install Now" button, which will trigger the real native dialog.
      setDeferredPrompt(e);
      globalDeferredPrompt = e;
      window.deferredPrompt = e;
      // showInstallPrompt stays true: the modal renders with the native action button.
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Listen for app installed event
    const installedHandler = () => {
      console.log('PWA was installed');
      localStorage.setItem('pwa-installed-date', new Date().toISOString());
      setShowInstallPrompt(false);
      setShowPWAReminder(false);
    };

    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const handleInstall = async () => {
    const promptEvent = window.deferredPrompt || globalDeferredPrompt || deferredPrompt;
    
    if (promptEvent) {
      promptEvent.prompt();
      const { outcome } = await promptEvent.userChoice;

      if (outcome === 'accepted') {
        console.log('User accepted install');
        localStorage.setItem('pwa-installed-date', new Date().toISOString());
        setShowInstallPrompt(false);
      } else {
        console.log('User dismissed install');
        // Don't set dismissed flag - we want to show again on next visit
      }

      window.deferredPrompt = null;
      globalDeferredPrompt = null;
      setDeferredPrompt(null);
    } else {
      // Fallback: Show standardized manual installation guide modal
      setShowManualGuide(true);
    }
  };

  const handleDismissInstall = () => {
    setShowInstallPrompt(false);
    // Throttle for 24 hours
    localStorage.setItem('pwa-prompt-dismissed-date', new Date().toISOString());
  };

  const handleDismissReminder = () => {
    setShowPWAReminder(false);
    // Store dismissal for current session only
    sessionStorage.setItem('pwa-reminder-dismissed', 'true');
  };

  if (!isActive) return null;

  // Manual Guide Modal (for browsers that don't support beforeinstallprompt)
  if (showManualGuide) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-[110] p-4 pb-safe">
        <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-sm p-6 overflow-y-auto max-h-[90vh]">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-xl">
                <Smartphone className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-bold text-gray-900 text-lg">
                {t('pwa.add_to_home', 'Add to Home Screen')}
              </h3>
            </div>
            <button onClick={() => setShowManualGuide(false)} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-gray-500 mb-5">
            {t('pwa.manual_guide_desc', 'Follow these 3 easy steps to install GigShip on your device:')}
          </p>
          <ol className="space-y-4">
            <li className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
              <div>
                <p className="font-semibold text-gray-800">
                  {t('pwa.step1_title', 'Open Browser Menu')}
                </p>
                <p className="text-sm text-gray-500">
                  {t('pwa.step1_desc', 'Tap the menu icon (⋮ or ⋯ or Share ↑) in your browser')}
                </p>
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
              <div>
                <p className="font-semibold text-gray-800">
                  {t('pwa.step2_title', 'Add to Home Screen')}
                </p>
                <p className="text-sm text-gray-500">
                  {t('pwa.step2_desc', 'Select "Add to Home Screen" or "Install App" from the menu')}
                </p>
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
              <div>
                <p className="font-semibold text-gray-800">
                  {t('pwa.step3_title', 'Install')}
                </p>
                <p className="text-sm text-gray-500">
                  {t('pwa.step3_desc', 'Tap "Add" or "Install" to confirm. GigShip will appear on your home screen!')}
                </p>
              </div>
            </li>
          </ol>
          <button
            onClick={() => setShowManualGuide(false)}
            className="mt-6 w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition"
          >
            {t('pwa.got_it', 'Got it!')}
          </button>
        </div>
      </div>
    );
  }

  // Install Prompt
  if (showInstallPrompt && !sessionStorage.getItem('pwa-reminder-dismissed')) {
    return (
      <div className="fixed bottom-0 left-0 right-0 flex items-end justify-center z-[110] p-4 pb-safe pointer-events-none">
        <div className="bg-white w-full max-w-sm rounded-lg shadow-2xl border-2 border-blue-500 p-4 animate-slide-up pointer-events-auto">
          <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Download className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">{t('pwa.install_title', 'Install GigShip App')}</h3>
              <p className="text-sm text-gray-600">{t('pwa.install_subtitle', 'Quick access from your home screen')}</p>
            </div>
          </div>
          <button
            onClick={handleDismissInstall}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-2 text-sm text-gray-700 mb-4 bg-blue-50 p-3 rounded">
          <p className="flex items-center"><span className="text-green-600 mr-2">✓</span>{t('pwa.benefit_instant', 'Instant access - no browser needed')}</p>
          <p className="flex items-center"><span className="text-green-600 mr-2">✓</span>{t('pwa.benefit_offline', 'Works offline')}</p>
          <p className="flex items-center"><span className="text-green-600 mr-2">✓</span>{t('pwa.benefit_notifs', 'Push notifications for updates')}</p>
          <p className="flex items-center"><span className="text-green-600 mr-2">✓</span>{t('pwa.benefit_speed', 'Faster performance')}</p>
        </div>

        <div className="flex space-x-2">
          <button
            onClick={handleDismissInstall}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 font-medium"
          >
            {t('pwa.maybe_later', 'Maybe Later')}
          </button>
          <button
            onClick={handleInstall}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold shadow-md"
          >
            {t('pwa.install_now', 'Install Now')}
          </button>
        </div>
        </div>
      </div>
    );
  }

  // PWA Reminder (for users who have installed but are browsing via browser)
  if (showPWAReminder && !sessionStorage.getItem('pwa-reminder-dismissed')) {
    return (
      <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-lg shadow-2xl p-4 z-50 animate-slide-up">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <Smartphone className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold">{t('pwa.use_app_title', 'Use GigShip App')}</h3>
              <p className="text-sm text-white text-opacity-90">{t('pwa.use_app_subtitle', 'Better experience on the app')}</p>
            </div>
          </div>
          <button
            onClick={handleDismissReminder}
            className="text-white hover:text-gray-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm mb-4 text-white text-opacity-90">
          You have GigShip installed! Open it from your home screen for a faster, app-like experience.
        </p>

        <button
          onClick={handleDismissReminder}
          className="w-full px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-gray-100 font-semibold shadow-md"
        >
          Got it!
        </button>
      </div>
    );
  }

  return null;
};

export default InstallPrompt;